import { initContract } from '@ts-rest/core';
import { z } from 'zod';

import { llmOutputTypesSchema } from '@eluve/graphql-types';
import { dynamicArtifactTemplateSchema } from '@eluve/llm-outputs';

const c = initContract();

export const llmContract = c.router(
  {
    generateAppointmentOutput: {
      method: 'POST',
      path: ':appointmentId/output',
      // TODO(marsela)[ELU-3411]: make user inputs here typesafe
      body: z.object({
        outputType: llmOutputTypesSchema,
        userInputs: z.record(z.string(), z.string()).optional(),
      }),
      responses: {
        201: z.object({
          jobId: z.string(),
        }),
        422: z.object({ message: z.string() }),
      },
    },
    summarizeAppointment: {
      method: 'POST',
      path: ':appointmentId/summarize',
      body: z
        .object({
          promptModelArgId: z.string().uuid().optional(),
        })
        .optional(),
      responses: {
        201: z.object({
          jobId: z.string(),
        }),
      },
    },
    summarizeAppointmentWithMultipleTemplates: {
      method: 'POST',
      path: ':appointmentId/summarize-many',
      body: z
        .object({
          summarizationDetails: z.array(
            z.object({
              promptTemplateId: z.string(),
              outputTemplateId: z.string().optional(),
            }),
          ),
        })
        .required(),
      responses: {
        201: z.object({
          jobIds: z.array(z.string()),
        }),
      },
    },
    resummarizeAppointmentWithUserInstructions: {
      method: 'POST',
      path: ':appointmentId/resummarize-with-instructions',
      body: z.object({
        // TODO(marsela)[ELU-3732]: update the API to require llmOutputId
        llmOutputId: z.string().nullable(),
        userInstruction: z.string(),
      }),
      responses: {
        201: z.object({
          jobId: z.string().optional(),
        }),
      },
    },

    generateInsights: {
      method: 'POST',
      path: ':appointmentId/insights',
      body: z
        .object({
          enableTools: z.boolean().optional(),
        })
        .optional(),
      responses: {
        201: z.object({
          jobId: z.string(),
        }),
      },
    },

    generateDiagnosisJustification: {
      method: 'POST',
      path: ':appointmentId/diagnosis-justification',
      body: z.object({
        targetDiagnosis: z.string(),
      }),
      responses: {
        201: z.object({
          jobId: z.string(),
        }),
        422: z.object({ message: z.string() }),
      },
    },

    createTemplateBundle: {
      method: 'POST',
      path: 'template-bundles',
      body: z.object({
        name: z.string().min(1),
        description: z.string().optional(),
        outputTemplate: dynamicArtifactTemplateSchema,
        // template_bundle_default_models id; when omitted, the resolved
        // prompt template's default model is used
        modelPresetId: z.string().uuid().optional(),
        // Draft templates are saved without an active version and stay
        // disabled until a version is published
        isDraft: z.boolean().default(false),
      }),
      responses: {
        201: z.object({
          outputTemplateId: z.string(),
        }),
        422: z.object({ message: z.string() }),
      },
    },

    saveTemplateBundleVersion: {
      method: 'POST',
      path: 'template-bundles/:outputTemplateId/versions',
      body: z.object({
        outputTemplate: dynamicArtifactTemplateSchema,
        // true updates (or creates) the caller's working draft; false
        // publishes a new version and makes it the active one
        isDraft: z.boolean().default(false),
        variantNotes: z.string().optional(),
        name: z.string().min(1).optional(),
        description: z.string().optional(),
        // template_bundle_default_models id; when provided, the bundle's
        // model selection is updated alongside the version
        modelPresetId: z.string().uuid().optional(),
        // active version the editor loaded; publish is rejected (409) if a
        // newer version has since become active
        basedOnVariantNumber: z.number().optional(),
      }),
      responses: {
        201: z.object({
          variantNumber: z.number(),
        }),
        409: z.object({ message: z.string() }),
        422: z.object({ message: z.string() }),
      },
    },

    activateTemplateBundleVersion: {
      method: 'POST',
      path: 'template-bundles/:outputTemplateId/versions/:variantNumber/activate',
      body: z.object({}).optional(),
      responses: {
        200: z.object({ success: z.literal(true) }),
        422: z.object({ message: z.string() }),
      },
    },

    generateTemplateBundleWithAi: {
      method: 'POST',
      path: 'template-bundles/generate-with-ai',
      body: z.object({
        noteFormat: z.enum(['SOAP', 'BIRP', 'DAP', 'GIRP']).default('SOAP'),
        specialty: z.string().trim().min(1).max(200),
        visitType: z.string().trim().min(1).max(200).optional(),
        noteLength: z
          .enum(['concise', 'balanced', 'detailed'])
          .default('balanced'),
        instructions: z.string().max(10_000).optional(),
      }),
      responses: {
        201: z.object({
          status: z.enum(['success', 'rejected']),
          outputTemplate: dynamicArtifactTemplateSchema.optional(),
          reason: z.string().optional(),
        }),
        422: z.object({ message: z.string() }),
      },
    },

    generateTemplateBundleFromNote: {
      method: 'POST',
      path: 'template-bundles/generate-from-note',
      body: z
        .object({
          // user_uploads id of an already-uploaded PDF note
          userUploadId: z.string().uuid().optional(),
          // pasted note text, used when no file is uploaded
          noteText: z.string().trim().min(1).max(100_000).optional(),
        })
        .refine(
          (body) => Boolean(body.userUploadId) !== Boolean(body.noteText),
          { message: 'Provide either userUploadId or noteText' },
        ),
      responses: {
        201: z.object({
          status: z.enum(['success', 'rejected']),
          outputTemplate: dynamicArtifactTemplateSchema.optional(),
          reason: z.string().optional(),
        }),
        422: z.object({ message: z.string() }),
      },
    },

    previewTemplateBundle: {
      method: 'POST',
      path: 'template-bundles/preview',
      body: z.object({
        outputTemplate: dynamicArtifactTemplateSchema,
        // template_bundle_default_models id; when omitted, the resolved
        // prompt template's default model is used
        modelPresetId: z.string().uuid().optional(),
        transcript: z.string().min(1).max(500_000),
        outputLanguage: z.string().optional(),
      }),
      responses: {
        201: z.object({
          success: z.boolean(),
          output: z.unknown().optional(),
          usage: z
            .object({
              inputTokens: z.number(),
              outputTokens: z.number(),
              // optional for skew safety: old pods omit it, client falls back
              durationMs: z.number().optional(),
            })
            .optional(),
        }),
        422: z.object({ message: z.string() }),
      },
    },

    regeneratePatientOverview: {
      method: 'POST',
      path: ':appointmentId/regenerate-patient-overview',
      body: z.object({}).optional(),
      responses: {
        201: z.object({
          scheduled: z.boolean(),
        }),
        422: z.object({ message: z.string() }),
      },
    },

    // [ELU-5500] Tenant-admin (clinic) note regeneration. Distinct from the
    // eluve-admin `adminAppointmentContract.regenerateSummary`: gated by the
    // ENABLE_TENANT_ADMIN_REGENERATE_NOTE tenant flag, an authoritative
    // per-tenant tenant-admin role check, and an eligibility gate (only when no
    // completed note exists — failed / never-generated / stuck). Note-only; no
    // re-transcription.
    tenantAdminRegenerateNote: {
      method: 'POST',
      path: ':appointmentId/tenant-admin-regenerate-note',
      body: z.object({
        templateBundleId: z.string().uuid(),
        outputLanguage: z.string().optional(),
      }),
      responses: {
        201: z.object({
          jobId: z.string(),
        }),
        403: z.object({ message: z.string() }),
        409: z.object({ message: z.string() }),
        422: z.object({ message: z.string(), reason: z.string() }),
      },
    },
  },

  { pathPrefix: '/:tenantId/llm/' },
);
